import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import useFkLookup from "./useFkLookup";
import FkPopover from "./FkPopover";

/**
 * FK input: text field (keeps the raw id value) + a search button that opens
 * a typeahead popover. Selecting an option fills the id.
 */
export default function FkPicker({ schema, name, column, value, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);
  const searchRef = useRef(null);

  const { label, opts, loading } = useFkLookup({ schema, name, column, value, open, q });

  // Close popover when clicking outside
  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Autofocus the search input when opening
  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const pick = (opt) => {
    onChange(opt.id);
    setOpen(false);
    setQ("");
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex items-stretch gap-2">
        <input type="text" value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
          disabled={disabled} data-testid={`field-${column}`} placeholder="ID"
          className="w-28 px-3 py-2 text-sm border border-[#E6E1D6] rounded font-mono outline-none focus:border-[#0F1829] disabled:bg-[#F5F2EB]" />
        {label && (
          <span className="flex-1 px-3 py-2 text-sm border border-dashed border-[#E6E1D6] rounded bg-[#FAF8F2] text-[#374151] truncate"
            title={label} data-testid={`field-${column}-label`}>
            {label}
          </span>
        )}
        {!disabled && (
          <button type="button" onClick={() => setOpen((v) => !v)}
            data-testid={`field-${column}-search-btn`}
            className="px-3 py-2 text-sm border border-[#E6E1D6] rounded hover:bg-[#F5F2EB] flex items-center gap-1 whitespace-nowrap">
            <Search size={13} /> Pesquisar
          </button>
        )}
      </div>
      {open && !disabled && (
        <FkPopover column={column} q={q} setQ={setQ} searchRef={searchRef}
          loading={loading} opts={opts} onPick={pick} />
      )}
    </div>
  );
}
